import { ReactNode, createContext, useContext, useMemo, useState } from 'react';

import { errorMessage } from '@/utils';

export type ErrorContextType = {
    error: { message: string; timestamp: Date } | null;
    createErrorHandler: (context: string) => (error: string | { message: string }) => void;
};

const defaultErrorContextData: ErrorContextType = {
    error: null,
    createErrorHandler: () => () => {
        throw new Error('No error handler outside ErrorContext');
    },
} as const;

const ErrorContext = createContext(defaultErrorContextData);

export const ErrorContextProvider = ({ children }: { children: ReactNode }) => {
    const [error, setError] = useState<ErrorContextType['error']>(null);

    const contextValue: ErrorContextType = useMemo(
        () => ({
            error,
            createErrorHandler: (context) => (error) => {
                const message = errorMessage(error, 'Unknown error');
                setError({ message: `Unable to ${context}: ${message}`, timestamp: new Date() });
            },
        }),
        [error]
    );

    return <ErrorContext.Provider value={contextValue}>{children}</ErrorContext.Provider>;
};

export const useErrorContext = () => useContext(ErrorContext);
